// src/pages/meeting/MeetingPlanShell.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Calendar, {
  buildCounts,
  getKRHolidayMap,
} from "../../../components/common/feature/Calendar";
import BoardTable from "../../../components/common/board/BoardTable";
import BoardPagination from "../../../components/common/board/BoardPagination";
import { format } from "date-fns";
import { meetingApi, getMyUserIdFromToken } from "../../../api/meeting";
import api from "../../../api/axios";
import CreatePanel from "../../../components/meeting/panels/CreatePanel";
import DetailPanel from "../../../components/meeting/panels/DetailPanel";
import EditPanel from "../../../components/meeting/panels/EditPanel";
import { createPortal } from "react-dom";
import SidePanel from "../../../components/common/layout/SidePanel";
import PageHeader from "../../../components/common/ui/PageHeader";
import BoardToolbar from "../../../components/common/board/BoardToolbar";

// ---------- 유틸 ----------
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toDateStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function normalizeStr(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
}
function buildCompositeKey(title, iso, location) {
  return `${normalizeStr(title)}|${iso}|${normalizeStr(location)}`;
}
const isUrl = (v = "") => /^https?:\/\//i.test(v);

function resolveLocationType(row) {
  const raw = row?.location_type;
  if (raw === "online" || raw === "offline_room" || raw === "offline_address") {
    return raw;
  }
  const loc = row?.location || "";
  if (!loc) return "offline_address";
  if (isUrl(loc)) return "online";
  return "offline_address";
}

function getLocationLabel(row) {
  const t = resolveLocationType(row);
  if (t === "online") return "온라인";
  if (t === "offline_room") return "오프라인(회의실)";
  return "오프라인(주소)";
}

// ---------- 페이지 셸 ----------
export default function MeetingPlanShell() {
  // 라우팅 파라미터/도구
  const { id: projectId, dns: dnsParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // 내 정보
  const myUserId = useMemo(() => Number(getMyUserIdFromToken() || 0), []);

  // 날짜/페이지 상태
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const dateStr = useMemo(() => toDateStr(selectedDate), [selectedDate]);

  // 데이터 상태
  const [loadingAll, setLoadingAll] = useState(false);
  const [allEvents, setAllEvents] = useState([]);

  // 사이드패널 상태 (create / view / edit)
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState(null); // 'view' | 'create' | 'edit'
  const [activeItem, setActiveItem] = useState(null);

  const openView = (item) => {
    setActiveItem(item);
    setPanelMode("view");
    setPanelOpen(true);
    // 목록에서 열었을 때도: 내가 참석 대상(O)이면서 아직 미응답이면 RSVP 모달 띄우기
    try {
      const mine = (item?.participants || []).find(
        (p) => Number(p.user_id) === myUserId
      );
      if (item?.is_required_for_me && mine && !mine.responded) {
        setRsvpOpen(true);
      } else {
        setRsvpOpen(false);
      }
    } catch {}
  };
  const openCreate = () => {
    setActiveItem(null);
    setPanelMode("create");
    setPanelOpen(true);
  };
  const openEdit = (item) => {
    setActiveItem(item);
    setPanelMode("edit");
    setPanelOpen(true);
  };
  const closePanel = () => {
    setPanelOpen(false);
    setPanelMode(null);
    setActiveItem(null);
    setRsvpOpen(false);
    navigate(`/project/${projectId}/meeting/plan`, { replace: true });
  };

  // RSVP 오버레이용 계산 (캘린더 영역 위치를 기준 anchor)
  const leftPanelRef = useRef(null);
  const [leftRect, setLeftRect] = useState(null);
  const updateLeftRect = useCallback(() => {
    if (!leftPanelRef.current) return;
    const r = leftPanelRef.current.getBoundingClientRect();
    setLeftRect({
      left: r.left + window.scrollX,
      top: r.top + window.scrollY,
      width: r.width,
      height: r.height,
    });
  }, []);
  useEffect(() => {
    updateLeftRect();
    const onResize = () => updateLeftRect();
    const onScroll = () => updateLeftRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, [updateLeftRect, panelOpen, selectedDate, allEvents]);

  // 디테일 패널 열림 중이면 최신 아이템으로 동기화
  useEffect(() => {
    if (!activeItem || panelMode !== "view") return;
    const fresh = allEvents.find((m) => m.id === activeItem.id);
    if (fresh) setActiveItem(fresh);
  }, [allEvents, activeItem, panelMode]);

  // 페이징 상태
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 휴일 맵/카운트
  const countsByDate = useMemo(
    () => buildCounts(allEvents, (m) => m.schedule),
    [allEvents]
  );
  const [activeYear, setActiveYear] = useState(selectedDate.getFullYear());
  const [holidayMap, setHolidayMap] = useState({});
  const loadHolidays = useCallback(async (year) => {
    try {
      const map = await getKRHolidayMap(year);
      setHolidayMap((prev) => ({ ...prev, ...map }));
    } catch {}
  }, []);
  useEffect(() => {
    loadHolidays(activeYear);
  }, [activeYear, loadHolidays]);

  // 전체 목록 로딩
  const fetchAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      let url = "/meeting/meetings/";
      let params = {
        ordering: "schedule",
        ...(projectId ? { project: projectId } : {}),
        // FE에서 명시적으로도 삭제 문서 제외 요청(서버 신판/구판 모두 대응)
        include_deleted: 0,
      };
      const collected = [];
      while (url) {
        const { data } = await api.get(url, { params });
        params = undefined;
        if (Array.isArray(data.results)) {
          collected.push(...data.results);
          url = data.next
            ? data.next.replace(/^https?:\/\/[^/]+\/api/, "")
            : null;
        } else {
          url = null;
        }
      }
      // 서버가 구버전이어도(필드 미노출) 안전하게: is_deleted !== true 만 노출
      const visible = collected.filter((m) => m?.is_deleted !== true);
      visible.sort((a, b) =>
        String(a.schedule).localeCompare(String(b.schedule))
      );
      setAllEvents(visible);
    } catch (e) {
      console.error(e);
      setAllEvents([]);
    } finally {
      setLoadingAll(false);
    }
  }, [projectId]);
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // 쿼리파라미터(meeting, rsvp_reset)로 상세/RSVP 처리
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const resetRanRef = useRef(false);
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const mid = Number(sp.get("meeting"));
    const wantReset = ["1", "true", "True"].includes(sp.get("rsvp_reset"));
    if (!mid || !Array.isArray(allEvents) || allEvents.length === 0) return;

    const target = allEvents.find((m) => Number(m.id) === mid);
    if (!target) return;

    openView(target);

    const mine = (target.participants || []).find(
      (p) => Number(p.user_id) === myUserId
    );

    if (wantReset && !resetRanRef.current) {
      (async () => {
        try {
          await meetingApi.rsvpReset(mid);
          await fetchAll();
          setRsvpOpen(true);
          navigate(`/project/${projectId}/meeting/plan?meeting=${mid}`, {
            replace: true,
          });
        } catch (e) {
          console.error(e);
        } finally {
          resetRanRef.current = true;
        }
      })();
      return;
    }

    if (mine && !mine.responded) setRsvpOpen(true);
    else setRsvpOpen(false);
  }, [location.search, allEvents, myUserId, navigate, projectId, fetchAll]);

  // 🔥 DNS 기반 진입 처리
  // - 공통 URL: /project/:id/meeting/:doc_type/:dns/view   → useParams().dns 로 들어옴
  // - 구버전 URL: /project/:id/meeting/plan?dns=...        → location.search 로 들어옴
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const dnsFromQuery = sp.get("dns");
    const dns = dnsParam || dnsFromQuery;
    if (!dns) return; // dns 없음 → 아무 것도 안함
    if (loadingAll) return; // 로딩 중 → 대기
    if (!allEvents.length) return; // 로딩 끝인데 목록 없음 → 종료

    const target = allEvents.find((m) => m.dns === dns);
    if (!target) return; // dns 매칭 실패

    // 상세 패널을 즉시 열기
    openView(target);

    // 옛날 `/plan?dns=...` 링크로 들어온 경우에만 meeting id 기반 URL로 정규화
    if (dnsFromQuery) {
      navigate(`/project/${projectId}/meeting/plan?meeting=${target.id}`, {
        replace: true,
      });
    }
  }, [dnsParam, location.search, loadingAll, allEvents, navigate, projectId]);

  // 리스트/페이징 파생값
  const list = useMemo(() => {
    const filtered = allEvents.filter(
      (m) => (m.schedule || "").slice(0, 10) === dateStr
    );
    return filtered.sort((a, b) => {
      const ua = a?.is_urgent ? 1 : 0;
      const ub = b?.is_urgent ? 1 : 0;
      if (ua !== ub) return ub - ua;
      return String(a.schedule || "").localeCompare(String(b.schedule || ""));
    });
  }, [allEvents, dateStr]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [dateStr]);
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [list.length, page, pageSize]);

  // 중복 체크용 CompositeKey 집합
  const existingCompositeKeySet = useMemo(() => {
    try {
      return new Set(
        allEvents
          .map((e) => {
            try {
              const iso = new Date(e.schedule).toISOString();
              return buildCompositeKey(e.title, iso, e.location);
            } catch {
              return null;
            }
          })
          .filter(Boolean)
      );
    } catch {
      return new Set();
    }
  }, [allEvents]);

  // 패널 타이틀 + X 버튼
  const panelTitleText =
    panelMode === "create" ? "" : activeItem ? activeItem.title : "회의";
  const panelTitle = (
    <div className="flex items-center justify-between gap-4">
      {/* <span className="font-semibold">{panelTitleText}</span> */}
    </div>
  );

  return (
    <div className="p-6">
      {/* 페이지 헤더 */}
      <PageHeader>회의 일정 관리</PageHeader>

      <BoardToolbar
        right={
          <button onClick={openCreate} className="btn-create">
            회의 등록
          </button>
        }
      />

      {/* 로딩 */}
      {loadingAll ? (
        <p className="text-body">로딩 중...</p>
      ) : (
        <div className="flex gap-6 items-start">
          {/* 좌측: 달력 */}
          <div
            ref={leftPanelRef}
            className="rounded-xl border border-slate-200 bg-white p-4 w-[550px]"
          >
            <div className="flex justify-center">
              <Calendar
                value={selectedDate}
                onChange={setSelectedDate}
                countsByDate={countsByDate}
                holidayMap={holidayMap}
                onMonthChange={(d) => {
                  const y = d.getFullYear();
                  if (y !== activeYear) setActiveYear(y);
                }}
              />
            </div>
          </div>

          {/* 우측: 회의 목록 */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 flex-1 max-h-[600px] overflow-y-auto">
            <h3 className="text-body mb-4 font-semibold">
              {format(selectedDate, "yyyy-MM-dd")} 회의 목록
            </h3>

            <BoardTable
              keyField="id"
              rows={pagedRows}
              onRowClick={(row) => openView(row)}
              columns={[
                {
                  key: "time",
                  header: "시간",
                  render: (row) =>
                    row?.schedule
                      ? format(new Date(row.schedule), "HH:mm")
                      : "--:--",
                },
                {
                  key: "title",
                  header: "제목",
                  render: (row) => (
                    <span className="inline-flex items-center gap-2 max-w-full">
                      <span className="truncate">{row.title}</span>
                      {row.is_urgent && (
                        <span className="shrink-0 inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs text-red-700">
                          긴급
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  key: "location",
                  header: "장소",
                  render: (row) => (
                    <span className="inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-700">
                      {getLocationLabel(row)}
                    </span>
                  ),
                },
                {
                  key: "required",
                  header: "내 참석 여부",
                  render: (row) => (row?.is_required_for_me ? "O" : "X"),
                },
              ]}
              emptyText="회의가 없습니다."
            />

            <BoardPagination
              page={page}
              pageSize={pageSize}
              total={list.length}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      {/* 우측 패널 (Create / Detail / Edit) */}
      <SidePanel
        isOpen={panelOpen}
        onClose={closePanel}
        title={panelTitle}
        width={1000}
      >
        {panelMode === "create" && (
          <CreatePanel
            projectId={projectId}
            selectedDate={selectedDate}
            existingCompositeKeySet={existingCompositeKeySet}
            onSaved={(created) => {
              navigate(
                `/project/${projectId}/meeting/plan?meeting=${created.id}`,
                { replace: true }
              );
              fetchAll();
            }}
            onClose={closePanel}
          />
        )}

        {panelMode === "view" && activeItem && (
          <DetailPanel
            item={activeItem}
            projectId={projectId}
            myUserId={myUserId}
            onReload={fetchAll}
            onEdit={() => openEdit(activeItem)}
            onDeleted={() => {
              fetchAll();
              closePanel();
            }}
            onClose={closePanel}
          />
        )}

        {panelMode === "edit" && activeItem && (
          <EditPanel
            item={activeItem}
            projectId={projectId}
            existingCompositeKeySet={existingCompositeKeySet}
            onSaved={(updated) => {
              navigate(
                `/project/${projectId}/meeting/plan?meeting=${
                  updated?.id ?? activeItem?.id
                }`,
                { replace: true }
              );
              fetchAll();
            }}
            onClose={() => {
              navigate(
                `/project/${projectId}/meeting/plan?meeting=${activeItem?.id}`,
                { replace: true }
              );
              fetchAll();
            }}
          />
        )}
      </SidePanel>

      {/* RSVP 오버레이 */}
      {panelMode === "view" &&
        rsvpOpen &&
        activeItem &&
        createPortal(
          <div
            className="fixed z-[1200]"
            style={{
              left: `${leftRect?.left ?? 0}px`,
              top: "22%",
              width: `${leftRect?.width ?? 0}px`,
              height: `${leftRect?.height ?? 0}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
            }}
            onClick={() => setRsvpOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-slate-300 bg-white shadow-lg p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-lg font-semibold mb-2">
                이 회의에 참여하시겠습니까?
              </h4>
              <p className="text-sm text-slate-600 mb-5">
                {activeItem?.title} ·{" "}
                {activeItem?.schedule?.slice(0, 16)?.replace("T", " ")}
              </p>

              <div className="flex gap-2 justify-end">
                <button
                  className="btn-save"
                  onClick={async () => {
                    await meetingApi.rsvp(activeItem.id, true);
                    await fetchAll();
                    setRsvpOpen(false);
                  }}
                >
                  네
                </button>

                <button
                  className="btn-basic"
                  onClick={() => setRsvpOpen(false)}
                >
                  나중에 답할게요
                </button>

                <button
                  className="btn-delete"
                  onClick={async () => {
                    await meetingApi.rsvp(activeItem.id, false);
                    await fetchAll();
                    setRsvpOpen(false);
                  }}
                >
                  아니오
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
